import { Component } from "@/types";
import type { ScatterChartProperties } from "@/types/chart-properties";
import { ChartPropertyChange, commonChartHandler } from "./CommonChartHandler";
import { ChartDataPoint } from "../ChartDataManager";
import { colorMixin, axisMixin, animationMixin, mergeMixinResults } from "./mixins";

/**
 * Scatter chart specific command handler
 */
export class ScatterChartHandler {
  /**
   * Parse scatter chart specific commands
   */
  parseCommand(command: string, component: Component): ChartPropertyChange | null {
    // First try common commands
    const commonChanges = commonChartHandler.parseCommand(command, component);

    // Apply mixins
    const colorChanges = colorMixin.parseCommand(command, component);
    const axisChanges = axisMixin.parseCommand(command, component);
    const animationChanges = animationMixin.parseCommand(command, component);

    // Then apply scatter-specific commands
    const scatterChanges = this.parseScatterSpecificCommand(command, component);

    // Merge all changes
    const mixinChanges = mergeMixinResults(colorChanges, axisChanges, animationChanges);

    if (commonChanges || mixinChanges || scatterChanges) {
      return {
        ...commonChanges,
        properties: {
          ...commonChanges?.properties,
          ...mixinChanges,
          ...scatterChanges?.properties,
        },
        style: {
          ...commonChanges?.style,
          ...scatterChanges?.style,
        },
        data: commonChanges?.data || scatterChanges?.data,
        type: commonChanges?.type || scatterChanges?.type,
      };
    }

    return null;
  }

  private parseScatterSpecificCommand(command: string, component: Component): ChartPropertyChange | null {
    const lowerCommand = command.toLowerCase();
    const changes: ChartPropertyChange = {};

    // Point size
    if (this.isPointSizeCommand(lowerCommand)) {
      const sizeChanges = this.parsePointSizeCommand(command, component);
      if (sizeChanges) {
        changes.properties = { ...changes.properties, ...sizeChanges };
      }
    }

    // Point shape
    if (this.isPointShapeCommand(lowerCommand)) {
      const shapeChanges = this.parsePointShapeCommand(command);
      if (shapeChanges) {
        changes.properties = { ...changes.properties, ...shapeChanges };
      }
    }

    // Trend line
    if (this.isTrendLineCommand(lowerCommand)) {
      const trendChanges = this.parseTrendLineCommand(command, component);
      if (trendChanges) {
        changes.properties = { ...changes.properties, ...trendChanges };
      }
    }

    // Bubble mode (with z-axis)
    if (this.isBubbleCommand(lowerCommand)) {
      const bubbleChanges = this.parseBubbleCommand(command, component);
      if (bubbleChanges) {
        changes.properties = { ...changes.properties, ...bubbleChanges };
      }
    }

    // Clustering
    if (this.isClusterCommand(lowerCommand)) {
      const clusterChanges = this.parseClusterCommand(command, component);
      if (clusterChanges) {
        changes.properties = { ...changes.properties, ...clusterChanges };
      }
    }

    // Correlation
    if (this.isCorrelationCommand(lowerCommand)) {
      const correlationChanges = this.parseCorrelationCommand(command, component);
      if (correlationChanges) {
        changes.properties = { ...changes.properties, ...correlationChanges };
      }
    }

    // Density/heatmap overlay
    if (this.isDensityCommand(lowerCommand)) {
      const densityChanges = this.parseDensityCommand(command);
      if (densityChanges) {
        changes.properties = { ...changes.properties, ...densityChanges };
      }
    }

    // Categories/groups
    if (this.isCategoryCommand(lowerCommand)) {
      const categoryChanges = this.parseCategoryCommand(command, component);
      if (categoryChanges) {
        changes.properties = { ...changes.properties, ...categoryChanges };
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isPointSizeCommand(command: string): boolean {
    return /size|radius|bigger|smaller|large|small|dot|marker/.test(command);
  }

  private parsePointSizeCommand(command: string, component: Component): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};
    const currentSize = typeof component.properties?.dotSize === 'number'
      ? component.properties.dotSize
      : 5;

    // Specific size value
    const sizeMatch = command.match(/(?:point|dot|marker)\s+size\s+(?:of\s+)?(\d+(?:\.\d+)?)/i) ||
                     command.match(/size\s+(\d+(?:\.\d+)?)/i);
    if (sizeMatch) {
      changes.dotSize = parseFloat(sizeMatch[1]);
    }

    // Relative size adjustments
    if (command.includes("bigger") || command.includes("larger")) {
      if (command.includes("much")) {
        changes.dotSize = currentSize * 2;
      } else {
        changes.dotSize = currentSize * 1.5;
      }
    } else if (command.includes("smaller") || command.includes("tiny")) {
      if (command.includes("much") || command.includes("tiny")) {
        changes.dotSize = currentSize * 0.5;
      } else {
        changes.dotSize = currentSize * 0.75;
      }
    }

    // Preset sizes
    if (command.includes("large point") || command.includes("big dot")) {
      changes.dotSize = 10;
    } else if (command.includes("small point") || command.includes("tiny dot")) {
      changes.dotSize = 3;
    } else if (command.includes("medium") || command.includes("normal")) {
      changes.dotSize = 5;
    }

    // Variable size based on data
    if (command.includes("variable size") || command.includes("size by value")) {
      changes.variableSize = true;
      changes.sizeRange = [2, 20];
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isPointShapeCommand(command: string): boolean {
    return /shape|circle|square|diamond|triangle|star|cross|plus/.test(command);
  }

  private parsePointShapeCommand(command: string): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};

    const shapes = {
      circle: "circle",
      square: "square",
      diamond: "diamond",
      triangle: "triangle",
      star: "star",
      cross: "cross",
      plus: "plus",
      hexagon: "hexagon"
    };

    for (const [key, value] of Object.entries(shapes)) {
      if (command.includes(key)) {
        changes.pointShape = value;
        break;
      }
    }

    // Multiple shapes for categories
    if (command.includes("different shapes") || command.includes("vary shapes")) {
      changes.useMultipleShapes = true;
      changes.shapeField = "category";
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isTrendLineCommand(command: string): boolean {
    return /trend|regression|fit|correlation|r.squared/.test(command);
  }

  private parseTrendLineCommand(command: string, component: Component): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    if (command.includes("trend") || command.includes("regression")) {
      changes.showTrendLine = true;

      // Trend line type
      if (command.includes("linear") || command.includes("straight")) {
        changes.trendType = "linear";
      } else if (command.includes("polynomial") || command.includes("curve")) {
        changes.trendType = "polynomial";

        // Polynomial degree
        const degreeMatch = command.match(/degree\s+(\d+)/i);
        if (degreeMatch) {
          changes.polynomialDegree = parseInt(degreeMatch[1]);
        } else {
          changes.polynomialDegree = 2;
        }
      } else if (command.includes("exponential")) {
        changes.trendType = "exponential";
      } else if (command.includes("logarithmic")) {
        changes.trendType = "logarithmic";
      } else if (command.includes("power")) {
        changes.trendType = "power";
      } else {
        changes.trendType = "linear"; // Default
      }

      // Calculate trend line data
      if (currentData.length > 1 && changes.trendType === "linear") {
        const trendData = this.calculateLinearTrend(currentData);
        if (trendData) {
          changes.trendLineData = trendData;
        }
      }
    }

    // Show R-squared value
    if (command.includes("r-squared") || command.includes("r2") || command.includes("correlation")) {
      changes.showRSquared = true;
    }

    // Confidence interval
    if (command.includes("confidence") || command.includes("interval")) {
      changes.showConfidenceInterval = true;

      const percentMatch = command.match(/(\d+)\s*%/);
      if (percentMatch) {
        changes.confidenceLevel = parseInt(percentMatch[1]) / 100;
      } else {
        changes.confidenceLevel = 0.95;
      }
    }

    // Remove trend line
    if (command.includes("remove trend") || command.includes("hide trend")) {
      changes.showTrendLine = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private calculateLinearTrend(data: ChartDataPoint[]): { slope: number; intercept: number; r2: number } | null {
    if (data.length < 2) return null;

    const n = data.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;

    for (const point of data) {
      const x = point.x || 0;
      const y = point.y || 0;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
      sumY2 += y * y;
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Calculate R-squared
    const yMean = sumY / n;
    let ssRes = 0, ssTot = 0;

    for (const point of data) {
      const x = point.x || 0;
      const y = point.y || 0;
      const yPred = slope * x + intercept;
      ssRes += (y - yPred) ** 2;
      ssTot += (y - yMean) ** 2;
    }

    const r2 = 1 - (ssRes / ssTot);

    return { slope, intercept, r2 };
  }

  private isBubbleCommand(command: string): boolean {
    return /bubble|z.axis|third.dimension|size.by/.test(command);
  }

  private parseBubbleCommand(command: string, component: Component): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};

    if (command.includes("bubble")) {
      changes.chartVariant = "bubble";
      changes.variableSize = true;

      // Z-axis field
      const zFieldMatch = command.match(/z[\\s-]?(?:axis|value)\s+["']([^"']+)["']/i);
      if (zFieldMatch) {
        changes.zField = zFieldMatch[1];
      } else {
        changes.zField = "z";
      }

      // Bubble size range
      if (command.includes("large bubble")) {
        changes.sizeRange = [5, 40];
      } else if (command.includes("small bubble")) {
        changes.sizeRange = [2, 15];
      } else {
        changes.sizeRange = [3, 25];
      }

      // Opacity for overlapping bubbles
      changes.fillOpacity = 0.6;
    }

    // Size by specific field
    const sizeByMatch = command.match(/size\s+by\s+["']([^"']+)["']/i);
    if (sizeByMatch) {
      changes.sizeField = sizeByMatch[1];
      changes.variableSize = true;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isClusterCommand(command: string): boolean {
    return /cluster|group|kmeans|dbscan/.test(command);
  }

  private parseClusterCommand(command: string, component: Component): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    if (command.includes("cluster") || command.includes("group")) {
      changes.showClusters = true;

      // Number of clusters
      const clusterMatch = command.match(/(\d+)\s+cluster/i);
      if (clusterMatch) {
        changes.clusterCount = parseInt(clusterMatch[1]);
      } else {
        changes.clusterCount = 3; // Default
      }

      // Clustering algorithm
      if (command.includes("kmeans") || command.includes("k-means")) {
        changes.clusterAlgorithm = "kmeans";
      } else if (command.includes("dbscan")) {
        changes.clusterAlgorithm = "dbscan";
      } else if (command.includes("hierarchical")) {
        changes.clusterAlgorithm = "hierarchical";
      }

      // Visual clustering (color by cluster)
      changes.colorByCluster = true;

      // Show cluster centers
      if (command.includes("center") || command.includes("centroid")) {
        changes.showClusterCenters = true;
      }

      // Convex hull around clusters
      if (command.includes("hull") || command.includes("boundary")) {
        changes.showClusterHull = true;
      }
    }

    // Remove clustering
    if (command.includes("remove cluster") || command.includes("no cluster")) {
      changes.showClusters = false;
      changes.colorByCluster = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isCorrelationCommand(command: string): boolean {
    return /correlation|relationship|association/.test(command);
  }

  private parseCorrelationCommand(command: string, component: Component): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};

    if (command.includes("correlation") || command.includes("relationship")) {
      changes.showCorrelation = true;

      // Correlation coefficient
      changes.showCorrelationCoefficient = true;

      // Correlation strength visualization
      if (command.includes("strong")) {
        changes.correlationThreshold = 0.7;
      } else if (command.includes("weak")) {
        changes.correlationThreshold = 0.3;
      } else {
        changes.correlationThreshold = 0.5;
      }

      // Highlight correlated points
      if (command.includes("highlight")) {
        changes.highlightCorrelatedPoints = true;
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isDensityCommand(command: string): boolean {
    return /density|heat|contour|concentration/.test(command);
  }

  private parseDensityCommand(command: string): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};

    if (command.includes("density") || command.includes("heat")) {
      changes.showDensity = true;

      // Density visualization type
      if (command.includes("contour")) {
        changes.densityType = "contour";
      } else if (command.includes("heatmap")) {
        changes.densityType = "heatmap";
      } else {
        changes.densityType = "gradient";
      }

      // Density resolution
      if (command.includes("high resolution") || command.includes("detailed")) {
        changes.densityResolution = "high";
      } else if (command.includes("low resolution") || command.includes("fast")) {
        changes.densityResolution = "low";
      } else {
        changes.densityResolution = "medium";
      }

      // Opacity
      changes.densityOpacity = 0.3;
    }

    // Remove density
    if (command.includes("remove density") || command.includes("hide density")) {
      changes.showDensity = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isCategoryCommand(command: string): boolean {
    return /category|group|color.by|shape.by/.test(command);
  }

  private parseCategoryCommand(command: string, component: Component): Partial<ScatterChartProperties> | null {
    const changes: Partial<ScatterChartProperties> = {};

    // Color by category
    const colorByMatch = command.match(/color\s+by\s+["']([^"']+)["']/i);
    if (colorByMatch) {
      changes.colorField = colorByMatch[1];
      changes.colorByCategory = true;
    }

    // Shape by category
    const shapeByMatch = command.match(/shape\s+by\s+["']([^"']+)["']/i);
    if (shapeByMatch) {
      changes.shapeField = shapeByMatch[1];
      changes.useMultipleShapes = true;
    }

    // Category filter
    const filterMatch = command.match(/show\s+(?:only\s+)?["']([^"']+)["']/i);
    if (filterMatch) {
      changes.categoryFilter = filterMatch[1];
    }

    // Show all categories
    if (command.includes("show all") || command.includes("all categories")) {
      changes.categoryFilter = null;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Get scatter chart specific suggestions
   */
  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [
      ...commonChartHandler.getSuggestions(component),
      ...axisMixin.getSuggestions(component),
      ...colorMixin.getSuggestions(component),
    ];

    // Scatter-specific suggestions
    suggestions.push(
      "Make points bigger",
      "Use square shapes",
      "Add linear trend line",
      "Show correlation coefficient",
      "Convert to bubble chart",
      "Add 3 clusters",
      "Show density heatmap",
      "Color by category",
      "Add polynomial trend line degree 2",
      "Show confidence interval"
    );

    if (!component.properties?.showTrendLine) {
      suggestions.push("Add trend line with R-squared");
    }

    if (!component.properties?.variableSize) {
      suggestions.push("Size points by z-value");
    }

    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    if (currentData.length > 10) {
      suggestions.push("Show k-means clustering", "Add density contours");
    }

    return suggestions;
  }
}

export const scatterChartHandler = new ScatterChartHandler();
